// En producción la imagen se compila con REACT_APP_API_URL vacía: el API se
// pide al mismo dominio desde donde se abrió la app (`/api/...`), así una sola
// imagen sirve a todos los clientes (`<cliente>.icard.tallansoft.com`). `??` y
// no `||`: el string vacío es un valor válido, no "falta configurar".
export const BASE_API = process.env.REACT_APP_API_URL ?? "http://127.0.0.1:8000";
export const TOKEN = "token";

// PENDING (en cocina) → READY (cocina lo marcó "Listo" en su pantalla, falta
// llevarlo a la mesa) → DELIVERED (servido). El mozo puede saltar de PENDING
// a DELIVERED directo (ej. una bebida que sirve él mismo).
export const ORDER_STATUS = {
  PENDING: "PENDING",
  READY: "READY",
  DELIVERED: "DELIVERED",
};

// Tipos de mesa (tables/models.py → TABLE_KINDS). Las virtuales (Venta rápida
// = pide en el mostrador para llevar — el nombre que usa JV en Restaurant.pe —, WhatsApp = llegó por mensaje, Delivery = se envía) usan el mismo flujo que una mesa del salón; el cobro
// guarda su canal (Payment.channel) para "Por canal de venta".
export const TABLE_KINDS = [
  { value: "SALON", text: "Salón", icon: "utensils" },
  { value: "TAKEAWAY", text: "Venta rápida", icon: "shopping bag" },
  { value: "WHATSAPP", text: "WhatsApp", icon: "whatsapp" },
  { value: "DELIVERY", text: "Delivery", icon: "motorcycle" },
];
// Canal de un pedido de delivery (tables/models.py → DELIVERY_CHANNELS).
export const DELIVERY_CHANNELS = [
  { value: "PHONE", text: "Teléfono", icon: "phone" },
  { value: "WHATSAPP", text: "WhatsApp", icon: "whatsapp" },
  { value: "PEDIDOSYA", text: "PedidosYa", icon: "motorcycle" },
  { value: "RAPPI", text: "Rappi", icon: "motorcycle" },
  { value: "DIDI", text: "DiDi Food", icon: "motorcycle" },
  { value: "OTHER", text: "Otro", icon: "ellipsis horizontal" },
];
export const getDeliveryChannel = (value) =>
  DELIVERY_CHANNELS.find((channel) => channel.value === value);

export const getTableKind = (value) =>
  TABLE_KINDS.find((kind) => kind.value === value) || TABLE_KINDS[0];

export const PAYMENT_STATUS = {
  PENDING: "PENDING",
  PAID: "PAID",
  // Venta cobrada que un Admin anuló después (ver "Anular" en Historial de
  // Pagos). Todo reporte filtra por PAID, así que deja de contar sola.
  VOID: "VOID",
};

// Motivos al cancelar un pedido pendiente (mismos valores que
// CancelReasonEnum en orders/models.py). TABLE_RELEASED no está: lo pone
// "Liberar mesa" solo, no se elige a mano.
export const CANCEL_REASONS = [
  { value: "CUSTOMER", text: "El cliente se arrepintió" },
  { value: "MISTAKE", text: "Error al tomar el pedido" },
  { value: "DELAY", text: "Demora" },
  { value: "OUT_OF_STOCK", text: "Producto agotado" },
  { value: "OTHER", text: "Otro" },
];

// Motivos de un descuento o cortesía al cobrar (texto libre en el backend).
export const DISCOUNT_REASONS = [
  "Cliente frecuente",
  "Cortesía de la casa",
  "Error de cocina",
  "Otro",
];

// Motivos al anular una venta ya cobrada (texto libre en el backend).
export const VOID_REASONS = [
  "Cobro duplicado",
  "Error en el monto o la forma de pago",
  "Devolución al cliente",
  "Otro",
];

// Formas de pago que cobra el local (una sola lista para toda la app: selector
// al pedir/generar la cuenta, íconos y reportes). LOSS ("Pérdida") no está
// acá a propósito: no es una forma de cobro, lo genera "Liberar mesa".
export const PAYMENT_METHODS = [
  { value: "CASH", text: "Efectivo", icon: "money bill alternate outline" },
  { value: "CARD", text: "Tarjeta", icon: "credit card outline" },
  { value: "YAPE", text: "Yape", icon: "mobile alternate" },
  { value: "PLIN", text: "Plin", icon: "mobile alternate" },
];

export function getPaymentMethod(value) {
  if (value === "LOSS") {
    return { value: "LOSS", text: "Pérdida", icon: "exclamation triangle" };
  }
  // Pago mixto (parte en una forma y parte en otra): no se elige al pedir
  // la cuenta, se arma al cobrar. El detalle va en payment.parts.
  if (value === "MIXED") {
    return { value: "MIXED", text: "Mixto", icon: "random" };
  }
  return PAYMENT_METHODS.find((method) => method.value === value) || null;
}

// { CASH: 20, YAPE: 30 } — cuánto de esta venta entró por cada forma de
// pago. Un pago mixto se reparte según sus partes; uno simple va entero a su
// forma. Usar esto (no `paymentType === "CASH"`) en todo total por forma de
// pago: si no, un pago mixto no se cuenta en ninguna.
export function getPaymentAmounts(payment) {
  if (payment.paymentType === "MIXED") {
    const amounts = {};
    (payment.parts || []).forEach((part) => {
      amounts[part.method] = (amounts[part.method] || 0) + Number(part.amount);
    });
    return amounts;
  }
  return { [payment.paymentType]: Number(payment.totalPayment) };
}

// "Efectivo S/ 20.00 + Yape S/ 30.00 (Op. 123)" — detalle de un pago mixto.
export function describePaymentParts(parts) {
  return (parts || [])
    .map(
      (part) =>
        `${getPaymentMethod(part.method)?.text} S/ ${Number(part.amount).toFixed(2)}${
          part.operation_code ? ` (Op. ${part.operation_code})` : ""
        }`
    )
    .join(" + ");
}

// Quién cobra (Caja): Admin y Súper Usuario siempre; un Estándar solo con
// "Puede cobrar (caja)". Mismo criterio que users/permissions.py, que lo
// exige en el backend.
export function canCharge(me) {
  return Boolean(me?.is_staff || me?.is_superuser || me?.can_charge);
}
